import React from 'react';
import { Users, ThumbsUp } from 'lucide-react';
import type { Game } from '../types';

interface GameListProps {
  games: Game[];
  viewMode: 'list' | 'grid';
  onSelectGame: (gameId: number) => void;
}

export function GameList({ games, viewMode, onSelectGame }: GameListProps) {
  const validGames = games.filter(game => 
    game && 
    typeof game.playerCount === 'number'
  );

  const sortedGames = [...validGames].sort((a, b) => b.playerCount - a.playerCount);

  const getRatingColor = (percentage: number = 0): string => {
    if (percentage >= 95) return 'text-emerald-600';
    if (percentage >= 85) return 'text-blue-600';
    if (percentage >= 70) return 'text-indigo-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Game</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Players</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categories</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedGames.map((game, index) => (
                <tr
                  key={game.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onSelectGame(game.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">#{index + 1}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img
                        src={game.imageUrl}
                        alt={game.name}
                        className="h-8 w-8 rounded object-cover"
                      />
                      <span className="ml-3 font-medium text-gray-900">{game.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-gray-900">
                      <Users className="h-4 w-4 mr-1 text-gray-500" />
                      <span className="font-medium">{formatNumber(game.playerCount)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {game.rating ? (
                      <div className="flex items-center">
                        <ThumbsUp className="h-4 w-4 mr-1" />
                        <span className={`font-medium ${getRatingColor(game.rating.percentage)}`}>
                          {game.rating.percentage}%
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">N/A</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {game.categories?.slice(0, 3).map((category) => (
                        <span
                          key={category}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                        >
                          {category}
                        </span>
                      ))}
                      {game.categories?.length > 3 && (
                        <span className="text-xs text-gray-500">
                          +{game.categories.length - 3} more
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {sortedGames.map((game, index) => (
        <div
          key={game.id}
          className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onSelectGame(game.id)}
        >
          <img
            src={game.imageUrl}
            alt={game.name}
            className="w-full h-40 object-cover"
          />
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
              {game.rating && (
                <span className={`text-sm font-medium ${getRatingColor(game.rating.percentage)}`}>
                  {game.rating.percentage}% Positive
                </span>
              )}
            </div>
            <h3 className="font-bold text-lg mb-2 text-gray-900">{game.name}</h3>
            <div className="flex items-center text-gray-600 mb-3">
              <Users className="w-4 h-4 mr-2" />
              <span className="font-medium">{formatNumber(game.playerCount)}</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {game.categories?.slice(0, 3).map((category) => (
                <span
                  key={category}
                  className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-800"
                >
                  {category}
                </span>
              ))}
              {game.categories?.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{game.categories.length - 3} more
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}